import React, { useState, useEffect } from "react";
import { Button, TextField, Autocomplete } from "@mui/material";
import axios from "axios";
import { APIURL } from "../../config";

/**
 * Shared filter controls for the reports.
 *
 * Compose whichever filters a report needs inside a <ReportFilterBar>, which supplies
 * the layout and the single SEARCH button:
 *
 *   <ReportFilterBar onSearch={() => fetchData()}>
 *     <OfficeLocationFilter comp_id={...} value={office} onChange={setOffice} />
 *     <ProgramFilter comp_id={...} value={program} onChange={setProgram} />
 *   </ReportFilterBar>
 *
 * Each report keeps its own selection state, since that is what its fetch reads when
 * building request params.
 */

// Shared option-loading for the filters below. `mapOption` turns an API row into
// the { value, title } shape the dropdowns render.
function useFilterOptions(url, comp_id, mapOption) {
    const [options, setOptions] = useState([]);

    useEffect(() => {
        const fetchOptions = async () => {
            if (!comp_id) return;
            try {
                const response = await axios.get(APIURL + url, {
                    params: { company_id: comp_id },
                });
                setOptions((Array.isArray(response.data) ? response.data : []).map(mapOption));
            } catch (err) {
                console.log(err);
            }
        };
        fetchOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, comp_id]);

    return options;
}

export function usePrograms(comp_id) {
    return useFilterOptions("/programs", comp_id, (r) => ({
        value: r.program_id,
        title: r.program_name || `Program #${r.program_id}`,
    }));
}

export function useOfficeLocations(comp_id) {
    return useFilterOptions("/company/officeLocation", comp_id, (r) => ({
        value: r.oloc_id,
        title: [r.oloc_addr_line1, r.oloc_addr_line2, r.oloc_city, r.oloc_state, r.oloc_zip]
            .filter(Boolean)
            .join(', '),
    }));
}

function FilterSelect(props) {
    return (
        <Autocomplete
            id={props.id}
            options={props.options}
            value={props.options.find((o) => o.value === props.value) || null}
            isOptionEqualToValue={(option, val) => option.value === val?.value}
            getOptionLabel={(option) => option?.title ?? ''}
            onChange={(event, value) => props.onChange(value ? value.value : null)}
            renderInput={(params) => <TextField {...params} label={props.label} />}
            sx={{ width: 250, ...props.sx }}
            size="small"
        />
    );
}

export function ProgramFilter(props) {
    const programs = usePrograms(props.comp_id);

    return (
        <FilterSelect
            id="program-select"
            label="Programs"
            options={programs}
            value={props.value}
            onChange={props.onChange}
            sx={props.sx}
        />
    );
}

export function OfficeLocationFilter(props) {
    const officeLocations = useOfficeLocations(props.comp_id);

    return (
        <FilterSelect
            id="office-location-select"
            label="Office Location"
            options={officeLocations}
            value={props.value}
            onChange={props.onChange}
            sx={props.sx}
        />
    );
}

export function ReportFilterBar(props) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...props.style }}>
            {props.children}
            {props.onSearch && (
                <Button onClick={props.onSearch} variant="contained">
                    SEARCH
                </Button>
            )}
        </div>
    );
}
